(ns arthur.flow.condition
  "Stage 4: reusable temporal conditioning of measured signals.

  It is a stage of its own for exactly one reason. `anchor avg` and `contour avg`
  are knobs and the rest of measure is not, so dragging either must not re-run the
  interior extraction — the one part of measure that reads a source pixel, and the
  only part that costs seconds.

  `anchor` smooths four transform parameters and `contours` smooths a ring track
  per vertex. Median rest positions, grid dwell and blink holds also operate on
  measurements without reading footage pixels or depending on a scene node."
  (:require [arthur.domain.geom :as geom]))

(defn anchor
  "Smooth the anchor fit's four parameters. `arthur.domain.geom/smooth-transforms`
  says why it is the transform and not the contour.

  Returns the measured map with `:transforms` replaced, so the anchor keeps
  travelling as one value and nothing downstream has to know whether it has been
  conditioned yet. `:residual` is deliberately left alone: it is the residual of
  the FIT, and it is not a function of this knob."
  [{:keys [anchor-avg]} anchored]
  (update anchored :transforms geom/smooth-transforms anchor-avg))

(defn contours
  "Temporal smoothing of a ring track, per vertex, across time.

  docs/design.md says to smooth the transform and never the contour. That was
  correct while keys were sparse: sampling at velocity minima rejected per-frame
  detector noise for free. With a key on every frame the noise is visible as a
  shimmer along the lip edge, so a bounded exception applies - the window must
  stay SHORTER than the shortest articulation worth keeping. At 12fps, mouth
  movement spans 3-6 frames and detector noise is per-frame, so a radius of 1
  separates them and a radius of 3 would start eating speech.

  `contour-avg` is in frames either side: 0 off, 1 = 3-frame average, 2 = 5-frame.
  It is the same clamped window `geom/moving-average` gives the transform
  parameters — reused rather than re-derived, so \"radius 2\" cannot come to mean
  two different things at the two knobs."
  [{:keys [contour-avg]} rings]
  (if (<= contour-avg 0)
    (vec rings)
    (let [rings (vec rings)
          axis  (fn [v k] (geom/moving-average (map #(k (nth % v)) rings) contour-avg))
          ;; Transposed once into a per-vertex pair of series, because the
          ;; smoothing is along time and the storage is along vertices.
          axes  (mapv (fn [v] [(axis v :x) (axis v :y)])
                      (range (count (first rings))))]
      (mapv (fn [t] (mapv (fn [[xs ys]] {:x (nth xs t) :y (nth ys t)}) axes))
            (range (count rings))))))

(defn median-point
  "A rest position per axis, resistant to a few extreme poses."
  [points]
  (let [median (fn [xs]
                 (let [v (vec (sort xs)) n (count v)]
                   (if (odd? n) (nth v (quot n 2))
                     (/ (+ (nth v (dec (quot n 2))) (nth v (quot n 2))) 2))))]
    {:x (median (map :x points)) :y (median (map :y points))}))

(defn quantize-snap
  "Snap a two-axis signal to a grid, accepting a new cell after its dwell."
  [points step dwell]
  (let [snap (fn [v] (* (js/Math.round (/ v step)) step))]
    (if (not (pos? step))
      (vec points)
      (let [q (mapv (fn [{:keys [x y]}] {:x (snap x) :y (snap y)}) points)]
        (loop [remaining q live (first q) pending (first q) run 0 out []]
          (if-let [p (first remaining)]
            (let [same? (= p pending)
                  pending (if same? pending p)
                  run (if same? (inc run) 1)
                  live (if (and (> run dwell) (not= pending live)) pending live)]
              (recur (rest remaining) live pending run (conj out live)))
            out))))))

(defn resolve-blink
  "Hysteresis, dwell and minimum shut hold. The hold is specified in source
  frames and is applied before a lower picture fps samples the result."
  [openness {:keys [cut dwell hold]}]
  (loop [readings openness live false run 0 held 0 out []]
    (if-let [v (first readings)]
      (let [reading (< v (if live (* cut 1.35) cut))
            run (if (= reading live) 0 (inc run))
            change? (and (> run dwell) (or (not live) (>= held hold)))
            live (if change? reading live)
            held (if change? 1 (inc held))]
        (recur (rest readings) live (if change? 0 run) held (conj out live)))
      out)))
